import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2075&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const MainTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 200px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #0d9488;
  text-align: center;
  font-style: italic;
`;

const CardButton = styled(motion.button)`
  background-color: #0d9488;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.3s ease;
  text-transform: uppercase;
  
  &:hover {
    background-color: #0f766e;
  }
`;

const BenefitsSection = styled.div`
  margin-top: 6rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  color: #e0e0e0;
  line-height: 1.5;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const Management: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const buttonVariants = {
    hover: { 
      scale: 1.05,
      backgroundColor: '#0f766e',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
      y: -5
    },
    tap: { scale: 0.95 }
  };
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Gestione | J-O-H' : 'Management | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizi di gestione degli affitti brevi per conto altrui: competenze nell\'ospitalità, promozione immobiliare, prenotazioni e cura degli ospiti.' 
            : 'Short-term rental management services on behalf of others: expertise in hospitality, property promotion, bookings, and guest care.'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-owner">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'GESTIONE' : 'MANAGEMENT'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'La gestione degli affitti brevi per conto altrui richiede una combinazione di competenze nell\'ambito dell\'ospitalità, della promozione immobiliare, della gestione delle prenotazioni e della cura delle esigenze degli ospiti, il tutto nel rispetto delle normative locali e delle leggi vigenti.'
              : 'Managing short-term rentals on behalf of others requires a combination of skills in hospitality, property promotion, booking management and taking care of guests\' needs, all in compliance with local regulations and current laws.'}
          </Description>
          
          <CardsContainer>
            <Card>
              <CardImage style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80)' }} />
              <CardTitle>{language === 'it' ? 'GESTIONE TOTALE' : 'TOTAL MANAGEMENT'}</CardTitle>
              <CardButton
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </CardButton>
            </Card>
            
            <Card>
              <CardImage style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80)' }} />
              <CardTitle>{language === 'it' ? 'GESTIONE PARZIALE' : 'PARTIAL MANAGEMENT'}</CardTitle>
              <CardButton
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
              >
                {language === 'it' ? 'Scopri' : 'Discover'}
              </CardButton>
            </Card>
          </CardsContainer>
          
          <MainTitle>{language === 'it' ? 'Come funzionano gli affitti brevi?' : 'How do short-term rentals work?'}</MainTitle>
          <Description>
            {language === 'it' 
              ? 'Sei stanco di affittare il tuo immobile alla vecchia maniera con contratti di lungo periodo, andando incontro al rischio di morosità e ad una pessima manutenzione? Affidati a J-O-H!'
              : 'Are you tired of renting your property the old-fashioned way with long-term contracts, running into the risk of arrears and very bad maintenance? Trust J-O-H!'}
          </Description>
          
          <BenefitsGrid>
            <BenefitCard>
              <IconContainer>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </IconContainer>
              <BenefitTitle>{language === 'it' ? 'Gestione completa' : 'Complete management'}</BenefitTitle>
              <BenefitDescription>
                {language === 'it' 
                  ? 'Risparmi tempo, pensiamo noi a tutto: annunci, accoglienza ospiti, adempimenti, pulizie...'
                  : 'Save time, we take care of everything: announcements, welcoming guests, obligations, cleaning...'}
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <IconContainer>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </IconContainer>
              <BenefitTitle>{language === 'it' ? 'Affitta a giornata' : 'Rent by the day'}</BenefitTitle>
              <BenefitDescription>
                {language === 'it' 
                  ? 'Il tuo immobile verrà affittato a turisti e lavoratori per soggiorni di pochi giorni.'
                  : 'Your property will be rented to tourists and workers for stays of a few days.'}
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <IconContainer>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </IconContainer>
              <BenefitTitle>{language === 'it' ? 'Totalmente legale' : 'Totally legal'}</BenefitTitle>
              <BenefitDescription>
                {language === 'it' 
                  ? 'La gestione degli immobili in affitto è disciplinata dalla legge Italiana.'
                  : 'The management of rental properties is governed by Italian law.'}
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <IconContainer>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </IconContainer>
              <BenefitTitle>{language === 'it' ? 'Nessun costo fisso' : 'No fixed costs'}</BenefitTitle>
              <BenefitDescription>
                {language === 'it' 
                  ? 'I proprietari apprezzano molto l\'obiettivo comune, la nostra remunerazione è completamente a successo e si basa sulle prenotazioni realizzate ed incassate.'
                  : 'The owners really appreciate the common goal, our remuneration is completely successful and is based on bookings made and collected.'}
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <IconContainer>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </IconContainer>
              <BenefitTitle>{language === 'it' ? 'Quantità di guadagno' : 'Earning potential'}</BenefitTitle>
              <BenefitDescription>
                {language === 'it' 
                  ? 'Valuteremo se il tuo immobile può essere gestito con gli affitti brevi e quanto può guadagnare.'
                  : 'We will evaluate whether your property can be managed with short-term rentals and how much it can earn.'}
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
          
          <BenefitsSection>
            <MainTitle>{language === 'it' ? 'I vantaggi degli affitti brevi' : 'The advantages of short-term rentals'}</MainTitle>
            <Description>
              {language === 'it' 
                ? 'Ti garantiamo la massima redditività senza pensieri. Affittando il tuo immobile a turisti e viaggiatori potrai sfruttare al massimo le potenzialità, garantendoti un incremento esponenziale degli incassi, eliminando definitivamente il rischio di morosità e garantendoti una cura ed una manutenzione continua nel tempo!'
                : 'We guarantee maximum profitability without worries. By renting your property to tourists and travelers, you will be able to make the most of its potential, guaranteeing an exponential increase in income, definitively eliminating the risk of arrears and guaranteeing continuous care and maintenance over time!'}
            </Description>
            
            <BenefitsGrid>
              <BenefitCard>
                <IconContainer>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </IconContainer>
                <BenefitTitle>{language === 'it' ? 'Maggiori guadagni' : 'Greater earnings'}</BenefitTitle>
                <BenefitDescription>
                  {language === 'it' 
                    ? 'La tua Casa ti darà un reddito maggiore rispetto ad affittarla con lunghi contratti.'
                    : 'Your home will give you a higher income than renting it on long contracts.'}
                </BenefitDescription>
              </BenefitCard>
              
              <BenefitCard>
                <IconContainer>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </IconContainer>
                <BenefitTitle>{language === 'it' ? 'Risparmia tempo' : 'Save time'}</BenefitTitle>
                <BenefitDescription>
                  {language === 'it' 
                    ? 'Pensiamo noi a gestire tutto, avrai più tempo libero e meno impegni.'
                    : 'We\'ll take care of everything, you\'ll have more free time and fewer commitments.'}
                </BenefitDescription>
              </BenefitCard>
              
              <BenefitCard>
                <IconContainer>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </IconContainer>
                <BenefitTitle>{language === 'it' ? 'Elimina morosità' : 'Eliminate arrears'}</BenefitTitle>
                <BenefitDescription>
                  {language === 'it' 
                    ? 'Non avrai più il rischio di inquilini che non pagano l\'affitto: gli ospiti pagano prima dell\'arrivo.'
                    : 'You will no longer have the risk of tenants not paying rent: guests pay before arrival.'}
                </BenefitDescription>
              </BenefitCard>
              
              <BenefitCard>
                <IconContainer>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </IconContainer>
                <BenefitTitle>{language === 'it' ? 'Piena flessibilità' : 'Full flexibility'}</BenefitTitle>
                <BenefitDescription>
                  {language === 'it' 
                    ? 'Non privarti della tua casa, potrai decidere di lasciarla libera in periodi in cui vuoi usarla per te o per amici.'
                    : 'Don\'t deprive yourself of your home, you can decide to leave it free during periods when you want to use it for yourself or for friends.'}
                </BenefitDescription>
              </BenefitCard>
              
              <BenefitCard>
                <IconContainer>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </IconContainer>
                <BenefitTitle>{language === 'it' ? 'Assicurazione danni' : 'Damage insurance'}</BenefitTitle>
                <BenefitDescription>
                  {language === 'it' 
                    ? 'Elimina il rischio di subire perdite per responsabilità civile o danni all\'immobile e furti.'
                    : 'Eliminates the risk of suffering losses due to civil liability or damage to the property and theft.'}
                </BenefitDescription>
              </BenefitCard>
              
              <BenefitCard>
                <IconContainer>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </IconContainer>
                <BenefitTitle>{language === 'it' ? 'Manutenzione continua' : 'Continuous maintenance'}</BenefitTitle>
                <BenefitDescription>
                  {language === 'it' 
                    ? 'L\'immobile viene costantemente controllato e curato nei minimi dettagli.'
                    : 'The property is constantly checked and taken care of down to the smallest details.'}
                </BenefitDescription>
              </BenefitCard>
            </BenefitsGrid>
          </BenefitsSection>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Management; 